import { useState } from "react";
import { X } from "lucide-react";
import { useGetAuth } from "../../hooks/useGetAuthHook";
import type { User } from "../../types/types";
import { createPortal } from "react-dom";
import { usePostAuth } from "../../hooks/usePostAuth";

interface CreatePostModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CreatePostModal = ({ isOpen, onClose }: CreatePostModalProps) => {
  const { data: user } = useGetAuth<User>("user");
  const [postContent, setPostContent] = useState("");

  const { mutate: createPost, isPending } = usePostAuth<
    any,
    { body: string; parent_id: null }
  >("posts", {
    queryKeyToInvalidate: ["posts"],

    onSuccess: () => {
      setPostContent("");
      onClose();
    },
  });

  const handleSubmit = () => {
    if (!postContent.trim() || isPending) return;

    createPost({
      body: postContent.trim(),
      parent_id: null,
    });
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <h2 className="text-xl font-semibold">Create post</h2>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 pt-4">
            <div className="flex items-center gap-3 mb-4">
              <img
                src={user?.photo_url || "/placeholder.svg"}
                alt={user?.name}
                className="h-12 w-12 rounded-full object-cover border-2 border-orange-200"
              />
              <div className="font-semibold capitalize">
                {user?.name || "John Doe"}
              </div>
            </div>

            <textarea
              value={postContent}
              onChange={(e) => setPostContent(e.target.value)}
              placeholder="What do you want to talk about?"
              className="w-full min-h-[200px] text-lg resize-none outline-none"
              autoFocus
            />
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t flex justify-end">
            <button
              onClick={handleSubmit}
              disabled={!postContent.trim() || isPending}
              className="px-8 py-2.5 rounded-full bg-orange-500 text-white font-semibold hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition"
            >
              {isPending ? "Posting..." : "Post"}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};
